import type { ResolvedModule } from '../../types/module.types';
import type { DomainEntityConfiguration } from '../../types/configuration';

interface EntityModulePath {
  tabId: string;
  /** Undefined when the tab itself is the entity */
  moduleId?: string;
  childModuleId?: string;
}

/**
 * Given an entity type, finds the tab, module, and optional entity path
 * segments needed to construct a URL. Searches the full module tree.
 *
 * Returns null if the entity type is not found in any module.
 */
export const findEntityModulePath = (
  modules: ResolvedModule[],
  entityType: string,
  entityConfigs: Record<string, Pick<DomainEntityConfiguration, 'type'>>,
): EntityModulePath | null => {
  // Build a reverse map: entity type → entity ID
  const entityIdByType = new Map<string, string>();
  Object.entries(entityConfigs).forEach(([entityId, config]) => {
    entityIdByType.set(config.type, entityId);
  });

  const entityId = entityIdByType.get(entityType);
  if (!entityId) return null;

  // Search the module tree for this entity ID
  let result: EntityModulePath | null = null;

  modules.some((tab) => {
    // Tab itself is the entity
    if (tab.type === 'entity' && tab.id === entityId) {
      result = { tabId: tab.id };
      return true;
    }

    const children = tab.modules ?? [];
    return children.some((child) => {
      // Direct entity under tab
      if (child.type === 'entity' && child.id === entityId) {
        result = { tabId: tab.id, moduleId: child.id };
        return true;
      }
      // Entity inside a container
      const grandchildren = child.modules ?? [];
      return grandchildren.some((grandchild) => {
        if (grandchild.type === 'entity' && grandchild.id === entityId) {
          result = {
            tabId: tab.id,
            moduleId: child.id,
            childModuleId: grandchild.id,
          };
          return true;
        }
        return false;
      });
    });
  });

  return result;
};
